import React, { useEffect, useRef } from 'react'
import { SvgCross } from '../assets/images'
import {
  getFocusableElements,
  restorePreviousFocus,
} from '../utils/tab-indexing'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  showCloseButton?: boolean
  children: React.ReactNode
  width?: string
}

const CustomDialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  showCloseButton,
  children,
  width = 'w-fit max-w-[98%]',
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog) {
      if (isOpen) {
        previouslyFocusedElementRef.current =
          document.activeElement as HTMLElement
        dialog.showModal()
        setTimeout(() => {
          const firstFocusable = getFocusableElements(dialog)?.[0]
          firstFocusable?.focus()
        }, 0)
      } else {
        dialog.close()
        restorePreviousFocus(previouslyFocusedElementRef.current)
      }
    }
    return () => {
      restorePreviousFocus(previouslyFocusedElementRef.current)
    }
  }, [isOpen])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === 'Tab') {
      const focusableElements = getFocusableElements(dialogRef.current)
      if (focusableElements.length === 0) return

      const first = focusableElements[0]
      const last = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      onClose?.()
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={onClose}
      className={`bg-white rounded-[1rem] shadow-lg m-auto overflow-y-auto p-3 ${width}`}
      onKeyDown={handleKeyDown}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        {showCloseButton ? (
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={onClose}
              className="absolute top-0 right-0 p-2 text-accent-500 hover:text-accent-700"
            >
              <SvgCross />
            </button>
          </div>
        ) : null}
        <div>{children}</div>
      </div>
    </dialog>
  )
}

export default CustomDialog
